import { memo, useEffect, useMemo, useState } from "react";
import { Box, Collapse, Typography, alpha, useTheme } from "@mui/material";
import {
  CheckCircleOutline as CheckIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { SubAgentEvent } from "src/views/assistant/types";
import { formatElapsed } from "src/reusable/utils/formatElapsed";

interface SubAgentActivityProps {
  personaName: string;
  status: string;
  messages: SubAgentEvent[];
  startedAt?: number;
  endedAt?: number;
}

interface ToolStep {
  name: string;
  statusMessage: string;
}

/** One timeline step per tool call, in order, deduped by tool_call_id so a
 *  call's streaming deltas collapse but repeated calls each keep a step. */
function extractToolSteps(messages: SubAgentEvent[]): ToolStep[] {
  const steps: ToolStep[] = [];
  const indexById = new Map<string, number>();
  for (const msg of messages) {
    const inner = msg.inner;
    if (!inner?.data) continue;

    if (inner.event === "message" && inner.data.content_type === "tool_call") {
      const text = inner.data.content?.text || "";
      try {
        const parsed = JSON.parse(
          text.replace(/^<tool_call>\s*/, "").replace(/\s*<\/tool_call>$/, ""),
        );
        const name = parsed.name || "Tool";
        const statusMessage =
          parsed.status_message || parsed.arguments?.status_message || "";
        const callId = parsed.tool_call_id;

        const existingIdx = callId != null ? indexById.get(callId) : undefined;
        if (existingIdx != null) {
          if (statusMessage) steps[existingIdx].statusMessage = statusMessage;
        } else {
          if (callId != null) indexById.set(callId, steps.length);
          steps.push({ name, statusMessage });
        }
      } catch {
        steps.push({ name: "Tool", statusMessage: "" });
      }
    }
  }
  return steps;
}

/** Check if the sub-agent is currently thinking (last content event is a think block). */
function isCurrentlyThinking(messages: SubAgentEvent[]): boolean {
  for (
    let i = messages.length - 1;
    i >= Math.max(0, messages.length - 5);
    i--
  ) {
    const inner = messages[i]?.inner;
    if (!inner?.data || inner.event !== "message") continue;
    const ct = inner.data.content_type;
    if (ct === "think") return true;
    if (ct === "tool_call" || ct === "tool_response") return false;
  }
  return false;
}

/**
 * SubAgentActivity renders compact, real-time progress for a sub-agent
 * running within the parent agent's chat. Shows a timeline of tool steps
 * with live status, elapsed time, and expand/collapse.
 *
 * Design: minimal, neutral, matches ArtifactCard/ThinkingContent visual language.
 */
const SubAgentActivity = memo(
  ({
    personaName,
    status,
    messages,
    startedAt,
    endedAt,
  }: SubAgentActivityProps) => {
    const [expanded, setExpanded] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const theme = useTheme();

    const isActive = status !== "completed" && status !== "error";
    const isError = status === "error";
    const toolSteps = useMemo(() => extractToolSteps(messages), [messages]);
    const thinking = useMemo(
      () => isActive && isCurrentlyThinking(messages),
      [messages, isActive],
    );

    // Current status text for the header
    const currentActivity = useMemo(() => {
      if (!isActive) {
        if (isError) return "Failed";
        const n = toolSteps.length;
        return n > 0 ? `${n} tool${n !== 1 ? "s" : ""} used` : "Completed";
      }
      if (thinking) return "Reasoning...";
      const lastStep = toolSteps[toolSteps.length - 1];
      if (lastStep) return lastStep.statusMessage || lastStep.name;
      return "Starting...";
    }, [isActive, isError, toolSteps, thinking]);

    // Elapsed comes from store timestamps, not mount time, so it survives the
    // card remounting when the turn finalizes. Ticks while active; endedAt
    // freezes it. Absent on hydrated cards — then we show no timer.
    useEffect(() => {
      if (!isActive || startedAt == null) return;
      const interval = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(interval);
    }, [isActive, startedAt]);

    const elapsed =
      startedAt == null
        ? null
        : Math.floor(((endedAt ?? now) - startedAt) / 1000);

    const hasSteps = toolSteps.length > 0;
    const canExpand = hasSteps || thinking;

    // Status-driven accent: blue while running, green when complete, red on error.
    const accent = isError
      ? theme.palette.error.main
      : isActive
        ? theme.palette.primary.main
        : theme.palette.success.main;
    const borderColor = alpha(accent, 0.3);
    const hoverBorder = alpha(accent, 0.5);
    const mutedText = alpha(theme.palette.text.primary, 0.45);
    const faintText = alpha(theme.palette.text.primary, 0.3);
    const subtleBg = alpha(accent, 0.05);

    // QA contract (docs/TESTID_INJECTION_SPEC.md): data-status is a tri-state
    // mirroring the indicator branches, regardless of raw backend status values.
    const dataStatus = isError ? "error" : isActive ? "started" : "completed";

    return (
      <Box
        data-testid="aa-subagent-card"
        data-status={dataStatus}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${borderColor}`,
          bgcolor: subtleBg,
          width: "100%",
          mb: 1,
          overflow: "hidden",
          transition: "border-color 0.2s ease",
          "&:hover": canExpand ? { borderColor: hoverBorder } : {},
        }}
      >
        {/* Header row */}
        <Box
          data-testid="aa-subagent-card-toggle"
          onClick={() => canExpand && setExpanded(!expanded)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.75,
            cursor: canExpand ? "pointer" : "default",
          }}
        >
          {/* Status indicator */}
          {isActive ? (
            <Box
              data-testid="aa-subagent-card-status"
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: theme.palette.primary.main,
                flexShrink: 0,
                animation: "subagent-dot 2s ease-in-out infinite",
                "@keyframes subagent-dot": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.35, transform: "scale(0.8)" },
                },
              }}
            />
          ) : isError ? (
            <ErrorIcon
              data-testid="aa-subagent-card-status"
              sx={{
                fontSize: 14,
                color: theme.palette.error.main,
                flexShrink: 0,
              }}
            />
          ) : (
            <CheckIcon
              data-testid="aa-subagent-card-status"
              sx={{
                fontSize: 14,
                color: theme.palette.success.main,
                flexShrink: 0,
              }}
            />
          )}

          {/* Agent name */}
          <Typography
            data-testid="aa-subagent-card-persona"
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            {personaName}
          </Typography>

          {/* Separator */}
          <Box
            sx={{
              width: 3,
              height: 3,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.text.primary, 0.15),
              flexShrink: 0,
            }}
          />

          {/* Current activity */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: mutedText,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}
          >
            {currentActivity}
          </Typography>

          {/* Elapsed time */}
          {elapsed != null && (
            <Typography
              sx={{
                fontSize: "0.6875rem",
                color: faintText,
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {formatElapsed(elapsed)}
            </Typography>
          )}

          {/* Expand chevron */}
          {canExpand &&
            (expanded ? (
              <ExpandIcon sx={{ fontSize: 16, color: faintText }} />
            ) : (
              <CollapseIcon sx={{ fontSize: 16, color: faintText }} />
            ))}
        </Box>

        {/* Indeterminate shimmer bar while active */}
        {isActive && (
          <Box
            sx={{
              height: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              position: "relative",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-40%",
                width: "40%",
                height: "100%",
                bgcolor: alpha(theme.palette.primary.main, 0.25),
                borderRadius: 1,
                animation: "subagent-shimmer 1.8s ease-in-out infinite",
              },
              "@keyframes subagent-shimmer": {
                "0%": { left: "-40%" },
                "100%": { left: "100%" },
              },
            }}
          />
        )}

        {/* Expanded: tool step timeline */}
        <Collapse in={expanded} timeout={150}>
          <Box
            data-testid="aa-subagent-card-messages"
            sx={{
              px: 1.25,
              py: 0.75,
              borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
            }}
          >
            {toolSteps.map((step, i) => {
              const isLast = i === toolSteps.length - 1;
              const isStepActive = isActive && isLast && !thinking;

              return (
                <Box
                  key={`${step.name}-${i}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.75,
                    py: 0.35,
                    pl: 0.25,
                  }}
                >
                  {/* Timeline dot */}
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      mt: "5px",
                      flexShrink: 0,
                      // Running step = blue (pulsing); finished steps = green;
                      // the step where the sub-agent failed = red.
                      bgcolor: isStepActive
                        ? theme.palette.primary.main
                        : isError && isLast
                          ? theme.palette.error.main
                          : theme.palette.success.main,
                      ...(isStepActive && {
                        animation: "subagent-dot 2s ease-in-out infinite",
                      }),
                    }}
                  />

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* Show the human-readable intent summary, not the raw
                        tool name. Fall back to a de-underscored tool name
                        only when no status message was provided. */}
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: alpha(theme.palette.text.primary, 0.7),
                        lineHeight: 1.4,
                      }}
                    >
                      {step.statusMessage || step.name.replace(/_/g, " ")}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Thinking indicator at the end of timeline */}
            {thinking && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  py: 0.35,
                  pl: 0.25,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: theme.palette.primary.main,
                    animation: "subagent-dot 2s ease-in-out infinite",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: mutedText,
                    fontStyle: "italic",
                    lineHeight: 1.4,
                  }}
                >
                  Reasoning...
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  },
);

export default SubAgentActivity;
